import moment from "moment";

/**
 * Display-side subset of the Angular app's consultation-entitlement util
 * (src/app/utils/consultation-entitlement.util.ts). Only what the booking
 * modal's limits card needs is ported; the booking/blocking decisions stay on
 * the server. Keep the heuristics identical to the Angular file — a coach
 * reading "Unlimited" in one place and "—" in the other would rightly not
 * trust either.
 */

export const ENTITLEMENT_NUMBER_PLACEHOLDER = "—";
export const UNLIMITED_CAP_LABEL = "Unlimited";
export const MONTHLY_CAP_NOT_SET_SUFFIX = "(Monthly cap has not been set)";

export interface EntitlementDisplayTotals {
  totalConsumingMonth?: number | null;
  totalConsumingProgram?: number | null;
  monthlyLimit?: number | null;
}

export interface ConsultationEntitlement {
  allowed?: boolean;
  blockReasonCode?: string | null;
  message?: string | null;
  remainingPlan?: number | null;
  remainingMonthly?: number | null;
  remainingProgram?: number | null;
  monthlyCap?: number | null;
  programCap?: number | null;
  countsMonth?: number | null;
  countsProgram?: number | null;
  countsPlan?: number | null;
  displayTotals?: EntitlementDisplayTotals | null;
  warnings?: string[] | null;
}

export interface DuplicateConsultationHint {
  blockingAppointmentId: string | number;
  appointmentStartTime?: string | null;
  appointmentEndTime?: string | null;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && !Number.isNaN(value);

export const extractEntitlement = (
  resp: any,
): ConsultationEntitlement | null => {
  const entitlement =
    resp?.data?.entitlement ??
    resp?.data?.consultationEntitlementSnapshot ??
    resp?.entitlement;
  if (!entitlement || typeof entitlement !== "object") return null;

  const out = { ...entitlement } as ConsultationEntitlement;
  // Some backend builds name the same figure countsPlan.
  if (out.countsProgram == null && out.countsPlan != null) {
    out.countsProgram = out.countsPlan;
  }
  return out;
};

export const hasEntitlementWarning = (
  entitlement: ConsultationEntitlement | null,
  code: string,
): boolean => {
  if (!Array.isArray(entitlement?.warnings)) return false;
  const want = String(code).trim().toUpperCase();
  return entitlement!.warnings!.some(
    (warning) => String(warning).trim().toUpperCase() === want,
  );
};

export const formatEntitlementNumber = (value: unknown): string =>
  isFiniteNumber(value) ? String(value) : ENTITLEMENT_NUMBER_PLACEHOLDER;

export const getProgramRemaining = (
  entitlement: ConsultationEntitlement | null,
): number | null => {
  if (!entitlement) return null;
  if (isFiniteNumber(entitlement.remainingProgram))
    return entitlement.remainingProgram;
  if (isFiniteNumber(entitlement.remainingPlan)) return entitlement.remainingPlan;
  return null;
};

// A null cap alongside month-scoped counts means the backend stored an
// effectively unlimited cap rather than none at all.
const monthlyCapLooksUnlimited = (entitlement: ConsultationEntitlement | null) => {
  if (!entitlement || isFiniteNumber(entitlement.monthlyCap)) return false;
  return (
    isFiniteNumber(entitlement.countsMonth) ||
    isFiniteNumber(entitlement.remainingMonthly)
  );
};

const programCapLooksUnlimited = (entitlement: ConsultationEntitlement | null) => {
  if (!entitlement || isFiniteNumber(entitlement.programCap)) return false;
  return (
    getProgramRemaining(entitlement) != null ||
    isFiniteNumber(entitlement.countsProgram) ||
    isFiniteNumber(entitlement.countsPlan)
  );
};

const monthlyCapNotConfigured = (entitlement: ConsultationEntitlement | null) => {
  if (monthlyCapLooksUnlimited(entitlement)) return false;
  return !(
    isFiniteNumber(entitlement?.monthlyCap) ||
    isFiniteNumber(entitlement?.displayTotals?.monthlyLimit)
  );
};

export const formatMonthlyCap = (
  entitlement: ConsultationEntitlement | null,
): string => {
  if (!entitlement) return ENTITLEMENT_NUMBER_PLACEHOLDER;
  if (isFiniteNumber(entitlement.monthlyCap)) return String(entitlement.monthlyCap);
  if (monthlyCapLooksUnlimited(entitlement)) return UNLIMITED_CAP_LABEL;
  if (monthlyCapNotConfigured(entitlement))
    return `${ENTITLEMENT_NUMBER_PLACEHOLDER} ${MONTHLY_CAP_NOT_SET_SUFFIX}`;
  return ENTITLEMENT_NUMBER_PLACEHOLDER;
};

export const formatProgramCap = (
  entitlement: ConsultationEntitlement | null,
): string => {
  if (!entitlement) return ENTITLEMENT_NUMBER_PLACEHOLDER;
  if (isFiniteNumber(entitlement.programCap)) return String(entitlement.programCap);
  if (programCapLooksUnlimited(entitlement)) return UNLIMITED_CAP_LABEL;
  return ENTITLEMENT_NUMBER_PLACEHOLDER;
};

export const getEntitlementSummaryLine = (
  entitlement: ConsultationEntitlement | null,
): string => {
  if (!entitlement) return "";

  const parts: string[] = [];
  if (isFiniteNumber(entitlement.remainingMonthly)) {
    parts.push(`${entitlement.remainingMonthly} consultation(s) remaining this month`);
  }

  const program = getProgramRemaining(entitlement);
  if (program != null) parts.push(`${program} remaining in program window`);

  if (parts.length) return parts.join(" · ");
  return entitlement.message?.trim() || "";
};

/**
 * The pending-consultation pill: set only when the server says booking is
 * blocked by an appointment this patient already has with this coach.
 */
export const extractDuplicateConsultationHint = (
  resp: any,
): DuplicateConsultationHint | null => {
  const outstanding = resp?.data?.outstandingPatientCoachConsultation;
  if (!outstanding || typeof outstanding !== "object") return null;
  if (outstanding.consultationBookingBlocked !== true) return null;

  const appointment = outstanding.blockingAppointment;
  if (!appointment || typeof appointment !== "object") return null;

  const id =
    appointment.health_coach_patient_appointment_id ??
    appointment.healthCoachPatientAppointmentId;
  if (id == null || String(id).trim() === "") return null;

  return {
    blockingAppointmentId: id,
    appointmentStartTime:
      appointment.appointment_start_time ?? appointment.appointmentStartTime,
    appointmentEndTime:
      appointment.appointment_end_time ?? appointment.appointmentEndTime,
  };
};

export const formatDuplicateConsultationWindow = (
  hint: DuplicateConsultationHint | null,
): string => {
  if (!hint) return "";

  const start = String(hint.appointmentStartTime ?? "").trim();
  const end = String(hint.appointmentEndTime ?? "").trim();
  if (!start && !end) return "";

  const parse = (value: string) => {
    const parsed = moment(value, [moment.ISO_8601, "YYYY-MM-DD HH:mm:ss"], true);
    return parsed.isValid() ? parsed : moment(value);
  };

  const from = start ? parse(start) : null;
  const to = end ? parse(end) : null;

  if (from?.isValid() && to?.isValid()) {
    const sameDay = from.format("YYYY-MM-DD") === to.format("YYYY-MM-DD");
    return sameDay
      ? `${from.format("MMM D, YYYY")} · ${from.format("h:mm a")} – ${to.format("h:mm a")}`
      : `${from.format("MMM D, YYYY h:mm a")} – ${to.format("MMM D, YYYY h:mm a")}`;
  }

  return [start, end].filter(Boolean).join(" – ");
};

/**
 * get_appointment_task doubles as the entitlement probe: the month around the
 * date being booked, flagged for an entitlement check.
 */
export const buildEntitlementProbePayload = ({
  patientId,
  healthCoachId,
  appointmentStart,
}: {
  patientId: string | number;
  healthCoachId: string | number;
  appointmentStart: string;
}) => {
  const probe = moment(appointmentStart, "YYYY-MM-DD HH:mm:ss");
  const date = probe.isValid() ? probe : moment();

  return {
    from_date: date.clone().startOf("month").format("YYYY-MM-DD"),
    to_date: date.clone().endOf("month").format("YYYY-MM-DD"),
    patient_id: patientId,
    health_coach_id: healthCoachId,
    for_entitlement_check: "Y",
    entitlement_appointment_start: appointmentStart,
    include_consultation_row_metadata: "Y",
  };
};
